/**
 * Seal / open sensitive workspace fields for at-rest encryption.
 *
 * Sealing runs only when the vault is unlocked AND local encryption policy
 * is on (`encryptLocal` / sealEnabled). Project names are sealed too.
 */

import {
  isSealed,
  isSealEnabled,
  isVaultUnlocked,
  openString,
  sealString,
} from '@/lib/crypto/vault';
import {
  isLegacyDefaultProjectInstructions,
  type Chat,
  type ContextItem,
  type MemoryItem,
  type Message,
  type Project,
  type WorkspaceSettings,
} from './types';

function shouldSeal(): boolean {
  return isVaultUnlocked() && isSealEnabled();
}

/** Seal sensitive fields on workspace settings (global instructions). */
export async function sealSettings(
  settings: WorkspaceSettings,
): Promise<WorkspaceSettings> {
  if (!shouldSeal()) return settings;
  const gi = settings.globalInstructions;
  if (gi == null || gi === '') return settings;
  return {
    ...settings,
    globalInstructions: await sealString(gi),
  };
}

/** Open sealed settings fields. Safe if plaintext or empty. */
export async function openSettings(
  settings: WorkspaceSettings,
): Promise<WorkspaceSettings> {
  if (settings.globalInstructions == null || settings.globalInstructions === '') {
    return settings;
  }
  return {
    ...settings,
    globalInstructions: await openString(settings.globalInstructions),
  };
}

export function settingsLookSealed(s: WorkspaceSettings): boolean {
  return !!s.globalInstructions && isSealed(s.globalInstructions);
}

async function sealMemoryItems(items: MemoryItem[] | undefined): Promise<MemoryItem[] | undefined> {
  if (!items?.length) return items;
  return Promise.all(
    items.map(async (item) => ({
      ...item,
      title: item.title ? await sealString(item.title) : item.title,
      content: await sealString(item.content),
    })),
  );
}

async function openMemoryItems(items: MemoryItem[] | undefined): Promise<MemoryItem[] | undefined> {
  if (!items?.length) return items ?? [];
  return Promise.all(
    items.map(async (item) => ({
      ...item,
      title: item.title ? await openString(item.title) : item.title,
      content: await openString(item.content),
    })),
  );
}

export async function sealProject(project: Project): Promise<Project> {
  if (!shouldSeal()) return project;
  const contextItems: ContextItem[] = await Promise.all(
    project.contextItems.map(async (item) => ({
      ...item,
      title: item.title ? await sealString(item.title) : item.title,
      content: await sealString(item.content),
      meta: item.meta
        ? {
            ...item.meta,
            ref: item.meta.ref ? await sealString(item.meta.ref) : item.meta.ref,
            displayName: item.meta.displayName
              ? await sealString(item.meta.displayName)
              : item.meta.displayName,
            name: item.meta.name ? await sealString(item.meta.name) : item.meta.name,
            nip05: item.meta.nip05 ? await sealString(item.meta.nip05) : item.meta.nip05,
            cachedText: item.meta.cachedText
              ? await sealString(item.meta.cachedText)
              : item.meta.cachedText,
            picture: item.meta.picture
              ? await sealString(item.meta.picture)
              : item.meta.picture,
            image: item.meta.image ? await sealString(item.meta.image) : item.meta.image,
            about: item.meta.about ? await sealString(item.meta.about) : item.meta.about,
            snippet: item.meta.snippet
              ? await sealString(item.meta.snippet)
              : item.meta.snippet,
          }
        : item.meta,
    })),
  );
  return {
    ...project,
    name: await sealString(project.name),
    description: project.description
      ? await sealString(project.description)
      : project.description,
    instructions: await sealString(project.instructions),
    memoryItems: await sealMemoryItems(project.memoryItems),
    contextItems,
  };
}

export async function openProject(project: Project): Promise<Project> {
  const contextItems: ContextItem[] = await Promise.all(
    project.contextItems.map(async (item) => ({
      ...item,
      title: item.title ? await openString(item.title) : item.title,
      content: await openString(item.content),
      meta: item.meta
        ? {
            ...item.meta,
            ref: item.meta.ref ? await openString(item.meta.ref) : item.meta.ref,
            displayName: item.meta.displayName
              ? await openString(item.meta.displayName)
              : item.meta.displayName,
            name: item.meta.name ? await openString(item.meta.name) : item.meta.name,
            nip05: item.meta.nip05 ? await openString(item.meta.nip05) : item.meta.nip05,
            cachedText: item.meta.cachedText
              ? await openString(item.meta.cachedText)
              : item.meta.cachedText,
            picture: item.meta.picture
              ? await openString(item.meta.picture)
              : item.meta.picture,
            image: item.meta.image ? await openString(item.meta.image) : item.meta.image,
            about: item.meta.about ? await openString(item.meta.about) : item.meta.about,
            snippet: item.meta.snippet
              ? await openString(item.meta.snippet)
              : item.meta.snippet,
          }
        : item.meta,
    })),
  );
  const instructions = await openString(project.instructions);
  return {
    ...project,
    name: await openString(project.name),
    description: project.description
      ? await openString(project.description)
      : project.description,
    // Strip legacy baked-in persona so global instructions own the baseline
    instructions: isLegacyDefaultProjectInstructions(instructions) ? '' : instructions,
    memoryItems: await openMemoryItems(project.memoryItems),
    contextItems,
  };
}

export async function sealChat(chat: Chat): Promise<Chat> {
  if (!shouldSeal()) return chat;
  return {
    ...chat,
    title: await sealString(chat.title),
    instructionOverride: chat.instructionOverride
      ? await sealString(chat.instructionOverride)
      : chat.instructionOverride,
  };
}

export async function openChat(chat: Chat): Promise<Chat> {
  return {
    ...chat,
    title: await openString(chat.title),
    instructionOverride: chat.instructionOverride
      ? await openString(chat.instructionOverride)
      : chat.instructionOverride,
  };
}

async function sealAttachments(
  attachments: Message['attachments'],
): Promise<Message['attachments']> {
  if (!attachments?.length) return attachments;
  return Promise.all(
    attachments.map(async (a) => ({
      ...a,
      name: a.name ? await sealString(a.name) : a.name,
      data: await sealString(a.data),
    })),
  );
}

async function openAttachments(
  attachments: Message['attachments'],
): Promise<Message['attachments']> {
  if (!attachments?.length) return attachments;
  return Promise.all(
    attachments.map(async (a) => ({
      ...a,
      name: a.name ? await openString(a.name) : a.name,
      data: await openString(a.data),
    })),
  );
}

async function sealMentions(
  mentions: Message['mentions'],
): Promise<Message['mentions']> {
  if (!mentions?.length) return mentions;
  return Promise.all(
    mentions.map(async (m) => ({
      ...m,
      displayName: await sealString(m.displayName),
      nip05: m.nip05 ? await sealString(m.nip05) : m.nip05,
      picture: m.picture ? await sealString(m.picture) : m.picture,
    })),
  );
}

async function openMentions(
  mentions: Message['mentions'],
): Promise<Message['mentions']> {
  if (!mentions?.length) return mentions;
  return Promise.all(
    mentions.map(async (m) => ({
      ...m,
      displayName: await openString(m.displayName),
      nip05: m.nip05 ? await openString(m.nip05) : m.nip05,
      picture: m.picture ? await openString(m.picture) : m.picture,
    })),
  );
}

async function sealNostrContext(
  chips: Message['nostrContext'],
): Promise<Message['nostrContext']> {
  if (!chips?.length) return chips;
  return Promise.all(
    chips.map(async (c) => ({
      ...c,
      title: await sealString(c.title),
      ref: await sealString(c.ref),
      snippet: c.snippet ? await sealString(c.snippet) : c.snippet,
      cachedText: c.cachedText ? await sealString(c.cachedText) : c.cachedText,
      nip05: c.nip05 ? await sealString(c.nip05) : c.nip05,
      picture: c.picture ? await sealString(c.picture) : c.picture,
      image: c.image ? await sealString(c.image) : c.image,
    })),
  );
}

async function openNostrContext(
  chips: Message['nostrContext'],
): Promise<Message['nostrContext']> {
  if (!chips?.length) return chips;
  return Promise.all(
    chips.map(async (c) => ({
      ...c,
      title: await openString(c.title),
      ref: await openString(c.ref),
      snippet: c.snippet ? await openString(c.snippet) : c.snippet,
      cachedText: c.cachedText ? await openString(c.cachedText) : c.cachedText,
      nip05: c.nip05 ? await openString(c.nip05) : c.nip05,
      picture: c.picture ? await openString(c.picture) : c.picture,
      image: c.image ? await openString(c.image) : c.image,
    })),
  );
}

export async function sealMessage(message: Message): Promise<Message> {
  if (!shouldSeal()) return message;
  return {
    ...message,
    content: await sealString(message.content),
    attachments: await sealAttachments(message.attachments),
    mentions: await sealMentions(message.mentions),
    nostrContext: await sealNostrContext(message.nostrContext),
  };
}

export async function openMessage(message: Message): Promise<Message> {
  return {
    ...message,
    content: await openString(message.content),
    attachments: await openAttachments(message.attachments),
    mentions: await openMentions(message.mentions),
    nostrContext: await openNostrContext(message.nostrContext),
  };
}

export async function openProjects(list: Project[]): Promise<Project[]> {
  return Promise.all(list.map(openProject));
}

export async function openChats(list: Chat[]): Promise<Chat[]> {
  return Promise.all(list.map(openChat));
}

export async function openMessages(list: Message[]): Promise<Message[]> {
  return Promise.all(list.map(openMessage));
}

/** True if any sensitive field looks sealed (vault needed). */
export function projectLooksSealed(p: Project): boolean {
  return (
    isSealed(p.name) ||
    isSealed(p.instructions) ||
    p.contextItems.some((i) => isSealed(i.content)) ||
    (p.memoryItems ?? []).some((i) => isSealed(i.content))
  );
}

export function messageLooksSealed(m: Message): boolean {
  return (
    isSealed(m.content) ||
    (m.attachments ?? []).some((a) => isSealed(a.data) || (a.name ? isSealed(a.name) : false))
  );
}
